// src/pages/OrderDetails.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useAuth } from '../contexts/AuthContext';
import MobileOrderDetailsView from '../components/OrderDetails/MobileOrderDetailsView';
import DesktopOrderDetailsView from '../components/OrderDetails/DesktopOrderDetailsView';

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  // Redirect unauthenticated users
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        let query = supabase
          .from('orders')
          .select(`
            id,
            created_at,
            total_amount,
            payment_method,
            payment_screenshot,
            status,
            updated_at,
            shipping_address,
            user_id,
            order_items (
              id,
              name,
              color,
              size,
              quantity,
              price_at_purchase,
              image_url,
              product_id,
              product_color_id,
              products (
                id,
                name_ar,
                description_ar
              ),
              product_colors (
                id,
                color_id,
                colors (
                  id,
                  name
                ),
                product_color_images (
                  id,
                  image_url
                )
              )
            ),
            profiles:user_id (
              id,
              full_name,
              phone_number,
              email
            )
          `)
          .eq('id', id);

        // If not admin, only allow access to user's own orders
        if (!isAdmin) {
          query = query.eq('user_id', user.id);
        }

        const { data, error } = await query.single();
        
        if (error) {
          if (error.code === 'PGRST116') {
            // No rows returned - either order doesn't exist or user doesn't have access
            setError('الطلب غير موجود أو ليس لديك صلاحية لعرضه');
          } else {
            throw error;
          }
        } else {
          setOrder(data);
        }
      } catch (err) {
        console.error("Error fetching order:", err);
        setError('لا يمكن تحميل تفاصيل الطلب');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchOrder();
    }
  }, [id, user, isAdmin]);

  const updateOrderStatus = async (newStatus) => {
    // Only allow admins to update order status
    if (!isAdmin) {
      setError('ليس لديك صلاحية لتحديث حالة الطلب');
      return;
    }

    setUpdating(true);
    try {
      const { error } = await supabase
        .from('orders')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      setOrder(prev => ({
        ...prev,
        status: newStatus,
        updated_at: new Date().toISOString()
      }));

      console.log('Order status updated successfully');
    } catch (err) {
      console.error('Error updating order status:', err);
      setError('فشل في تحديث حالة الطلب');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusDetails = (status) => {
    switch (status) {
      case 'delivered':
        return { color: 'success', icon: <CheckCircleOutlineIcon />, label: 'تم التسليم' };
      case 'cancelled':
        return { color: 'error', icon: <HighlightOffIcon />, label: 'ملغى' };
      case 'shipped':
        return { color: 'info', icon: <LocalShippingIcon />, label: 'تم الشحن' };
      case 'confirmed':
        return { color: 'primary', icon: <ThumbUpIcon />, label: 'تم التأكيد' };
      default:
        return { color: 'warning', icon: <AccessTimeIcon />, label: 'قيد الانتظار' };
    }
  };

  const getProductImage = (item) => {
    if (item.image_url) {
      return item.image_url;
    }
    
    if (item.product_colors && 
        item.product_colors.product_color_images && 
        item.product_colors.product_color_images.length > 0) {
      return item.product_colors.product_color_images[0].image_url;
    }
    
    return null;
  };

  const getProductColor = (item) => {
    if (item.color) {
      return item.color;
    }
    
    if (item.product_colors && 
        item.product_colors.colors && 
        item.product_colors.colors.name) {
      return item.product_colors.colors.name;
    }
    
    return "لا يوجد";
  };

  const getProductName = (item) => {
    if (item.name) {
      return item.name;
    }
    
    if (item.products && item.products.name_ar) {
      return item.products.name_ar;
    }
    
    return "منتج غير معروف";
  };

  const getShippingAddress = () => {
    if (!order?.shipping_address) return null;
    
    try {
      if (typeof order.shipping_address === 'string') {
        return JSON.parse(order.shipping_address);
      }
      return order.shipping_address;
    } catch (error) {
      console.error('Error parsing shipping address:', error);
      return null;
    }
  };

  const calculateSubtotal = () => {
    if (!order) return 0;
    const shippingAddress = getShippingAddress();
    const shippingPrice = shippingAddress?.shippingPrice || 0;
    return order.total_amount - shippingPrice;
  };

  const getBackNavigationPath = () => {
    if (isAdmin) {
      return '/admin/orders';
    }
    return '/orders'; // or '/profile/orders' depending on your routing structure
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          {error || 'الطلب غير موجود'}
        </Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate(getBackNavigationPath())}>
          {isAdmin ? 'العودة إلى قائمة الطلبات' : 'العودة إلى طلباتي'}
        </Button>
      </Box>
    );
  }

  const shippingAddress = getShippingAddress();
  const shippingPrice = shippingAddress?.shippingPrice || 0;
  const subtotal = calculateSubtotal();

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2, md: 3 }, 
      maxWidth: 1200, 
      margin: '0 auto',
      pb: { xs: 3, md: 3 } // Reduced bottom padding since FAB is removed
    }}>
      {/* Header */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        bgcolor: 'primary.main',
        color: 'white',
        py: 2,
        zIndex: 10,
        mb: { xs: 2, sm: 3 },
        mx: { xs: 0.25, md: 0.5 },
        mt: { xs: 1, md: 2 },
        borderRadius: 1,
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          px: { xs: 2, md: 4 },
        }}>
          <Typography
            variant={isSmallMobile ? "h5" : "h4"}
            sx={{ 
              fontWeight: 'bold', 
              color: 'inherit', 
              flex: 1,
              textAlign: 'center',
            }}
          >
            طلب #{order.id.slice(0, 8)}
          </Typography>
          <IconButton 
            onClick={() => navigate(getBackNavigationPath())} 
            sx={{ 
              color: 'inherit', 
              ml: 1,
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <ArrowBack />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Conditionally render mobile or desktop view */}
      {isMobile ? (
        <MobileOrderDetailsView 
          order={order}
          updating={updating}
          updateOrderStatus={isAdmin ? updateOrderStatus : null} // Only allow admins to update status
          getStatusDetails={getStatusDetails}
          shippingAddress={shippingAddress}
          subtotal={subtotal}
          shippingPrice={shippingPrice}
          getProductImage={getProductImage}
          getProductName={getProductName}
          getProductColor={getProductColor}
          isAdmin={isAdmin} // Pass admin status to child components
        />
      ) : (
        <DesktopOrderDetailsView 
          order={order}
          updating={updating}
          updateOrderStatus={isAdmin ? updateOrderStatus : null} // Only allow admins to update status
          getStatusDetails={getStatusDetails}
          shippingAddress={shippingAddress}
          subtotal={subtotal}
          shippingPrice={shippingPrice}
          getProductImage={getProductImage}
          getProductName={getProductName}
          getProductColor={getProductColor}
          isAdmin={isAdmin} // Pass admin status to child components
        />
      )}
    </Box>
  );
};

export default OrderDetails;
